import { decodeOfflineToken, offlineAllowance, offlineInvoiceNo, offlineTokenNo } from '@shripos/shared/offline';
import { businessDate, financialYear } from '@shripos/shared/time';
import { api } from '../api.js';
import { useOffline } from '../../stores/offline.js';
import { kvGet, kvSet } from './db.js';

/**
 * The device's signed offline permit (Ed25519, from GET /sync/permit). Kept with the device's
 * own clock reading at the moment it arrived, so offline time is measured from the server's
 * clock and a clock moved backwards stops offline billing.
 */
async function verifySignature(token, publicKey) {
  const t = decodeOfflineToken(token);
  if (!t) return null;
  try {
    const raw = Uint8Array.from(
      atob(publicKey.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (publicKey.length % 4)) % 4)),
      (c) => c.charCodeAt(0),
    );
    const key = await crypto.subtle.importKey('raw', raw, { name: 'Ed25519' }, false, ['verify']);
    return (await crypto.subtle.verify({ name: 'Ed25519' }, key, t.signature, t.signed)) ? t.payload : null;
  } catch {
    // Browsers without Ed25519 in WebCrypto: the server re-checks every bill when it syncs.
    return t.payload;
  }
}

/** Fetch a fresh permit while online. Returns the stored permit (or null when not allowed). */
export async function refreshPermit() {
  try {
    const r = await api.get('/sync/permit');
    if (!r.enabled) {
      await kvSet('permit', { enabled: false, reason: r.reason });
      publish({ enabled: false, reason: r.reason });
      return null;
    }
    const payload = await verifySignature(r.token, r.publicKey);
    if (!payload) return null;
    const permit = { enabled: true, token: r.token, payload, localAtIssue: Date.now() };
    await kvSet('permit', permit);
    // The server's highest synced number for this device wins over a wiped local counter.
    const series = (await kvGet('series')) ?? {};
    if ((series[payload.fy] ?? 0) < payload.lastSeq)
      await kvSet('series', { ...series, [payload.fy]: payload.lastSeq });
    publish(permit);
    return permit;
  } catch (err) {
    if (err?.details?.reason === 'NOT_DEVICE') publish({ enabled: false, reason: 'NOT_DEVICE' });
    return null;
  }
}

function publish(permit) {
  const a = permit?.enabled ? offlineAllowance(permit.payload, { localAtIssue: permit.localAtIssue }) : null;
  useOffline.getState().set({
    permit: permit?.enabled
      ? { enabled: a.ok, reason: a.ok ? null : a.reason, exp: permit.payload.exp, remainingMs: a.remainingMs }
      : { enabled: false, reason: permit?.reason ?? 'NO_TOKEN' },
  });
}

/** Can this device bill offline right now? { ok, reason?, permit } */
export async function offlineCheck() {
  const permit = await kvGet('permit');
  if (!permit?.enabled) return { ok: false, reason: permit?.reason ?? 'NO_TOKEN' };
  const a = offlineAllowance(permit.payload, { localAtIssue: permit.localAtIssue });
  publish(permit);
  return a.ok ? { ok: true, permit, serverNow: a.serverNow } : { ok: false, reason: a.reason };
}

/**
 * Reserve the next offline invoice number (and a token unless the order already has one) for a
 * sale (device series, spec §4.3). Numbers are taken before the bill is stored, so a crash can
 * only skip a number, never repeat one.
 */
export async function nextOfflineNumbers(permit, catalog, at, { token = true } = {}) {
  const tz = catalog.outlet.timezone;
  const fy = financialYear(at, tz);
  const series = (await kvGet('series')) ?? {};
  const seq = Math.max(series[fy.code] ?? 0, fy.code === permit.payload.fy ? permit.payload.lastSeq : 0) + 1;
  await kvSet('series', { ...series, [fy.code]: seq });
  return {
    fy,
    seq,
    businessDate: businessDate(at, tz, catalog.outlet.dayCutoffMinutes ?? 240),
    invoiceNo: offlineInvoiceNo(permit.payload.format, { deviceCode: permit.payload.dcode, fy, seq }),
    tokenNo: token ? await nextOfflineToken(permit, catalog, at) : null,
  };
}

/** The next token from this device's range (device D006: 6001, 6002, …), restarting each business day. */
export async function nextOfflineToken(permit, catalog, at) {
  const day = businessDate(at, catalog.outlet.timezone, catalog.outlet.dayCutoffMinutes ?? 240);
  const tokens = (await kvGet('tokens')) ?? {};
  const n = (tokens.day === day ? tokens.n : 0) + 1;
  await kvSet('tokens', { day, n });
  return offlineTokenNo(permit.payload.dcode, n);
}

export async function loadPermitState() {
  publish(await kvGet('permit'));
}
